import {
  DEVELOPER_SPEEDS,
  DEVELOPER_TELEPORT_TARGET_IDS,
  ITEM_DEFINITIONS,
  ITEM_STACK_LIMIT_MAXIMUM,
  WORLD_MAX_COORDINATE,
  WORLD_MIN_COORDINATE,
  WORLD_REVIEW_DESTINATIONS,
  formatMass,
  getItemDefinition,
  getWorldRiverProfileAt,
  getWorldRoadProfileAt,
  sampleMacroBiomeAt,
  sampleWorldTerrainAt,
  type BiomeId,
  type DeveloperSpeed,
} from "@endclock/shared";
import type { AtmosphereFeatureFlags } from "../engine/atmosphere.js";
import type { DeveloperRenderFlags, EndclockGame } from "../game/game.js";
import { isEditableTarget } from "../game/input.js";
import type { ClientInventorySnapshot } from "../items/inventory-store.js";
import { DeveloperWorldRenderer } from "./dev-renderer.js";
import { LocalDeveloperState, type DeveloperOverlayState } from "./dev-state.js";
import { setupWarfareDeveloperTools } from "./warfare-tools.js";

const $ = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing developer UI element: ${selector}`);
  return element;
};

const BOOKMARK_KEY = "endclock.dev.bookmarks.v1";

export function setupDeveloperEnvironment(game: EndclockGame): LocalDeveloperState {
  setupWarfareDeveloperTools(game);
  const state = new LocalDeveloperState();
  const auth = $("#developer-auth") as HTMLElement;
  const authForm = $("#developer-auth-form") as HTMLFormElement;
  const password = $("#developer-token") as HTMLInputElement;
  const authState = $("#developer-auth-state") as HTMLElement;
  const panel = $("#developer-panel") as HTMLElement;
  const panelOpen = $("#developer-panel-open") as HTMLButtonElement;
  const panelClose = $("#developer-panel-close") as HTMLButtonElement;
  const commandState = $("#developer-command-state") as HTMLElement;
  const itemAdmin = $("#developer-items") as HTMLElement;
  const itemState = $("#developer-item-state") as HTMLElement;
  const streaming = $("#developer-streaming") as HTMLElement;
  const renderer = new DeveloperWorldRenderer($("#developer-world-overlay"), game, state);
  let authAttempted = false;
  let wasAuthorized = false;
  let streamingAfterTeleport = false;
  let biomeMapDrawn = false;
  const frameHistory: number[] = [];
  let debugLoopStarted = false;
  const renderWorldDebug = (): void => {
    if (!game.network.developer.authorized) {
      debugLoopStarted = false;
      renderer.render();
      return;
    }
    renderer.render();
    requestAnimationFrame(renderWorldDebug);
  };
  const startWorldDebug = (): void => {
    if (debugLoopStarted) return;
    debugLoopStarted = true;
    requestAnimationFrame(renderWorldDebug);
  };

  const capture = (element: Element): void => {
    for (const eventName of ["pointerdown", "pointermove", "pointerup", "touchstart", "touchmove", "touchend", "wheel"]) {
      element.addEventListener(eventName, (event) => event.stopPropagation(), { passive: eventName !== "touchmove" });
    }
  };
  [auth, panel, panelOpen, $("#developer-vertical-controls")].forEach(capture);

  const openAuth = (): void => {
    if (game.network.developer.authorized) return openPanel();
    auth.hidden = false;
    authState.textContent = "Session authorization required.";
    window.setTimeout(() => password.focus(), 0);
  };
  const closeAuth = (): void => {
    auth.hidden = true;
    password.value = "";
    password.blur();
    game.input.reset();
  };
  const openPanel = (): void => {
    if (!game.network.developer.authorized || state.screenshotMode) return;
    state.panelOpen = true;
    panel.hidden = false;
    panelOpen.hidden = true;
    game.input.reset();
  };
  const closePanel = (): void => {
    state.panelOpen = false;
    panel.hidden = true;
    panelOpen.hidden = !game.network.developer.authorized || state.screenshotMode;
    game.input.reset();
  };

  authForm.addEventListener("submit", (event) => {
    event.preventDefault();
    authAttempted = true;
    authState.textContent = "Authorizing with server…";
    game.authorizeDeveloper(password.value);
    password.value = "";
  });

  game.onDeveloperState = (developer, teleport) => {
    itemAdmin.hidden = !developer.authorized;
    if (!developer.authorized) itemState.textContent = "";
    if (developer.authorized) {
      const firstAuthorization = panelOpen.hidden && panel.hidden;
      closeAuth();
      panelOpen.hidden = state.screenshotMode;
      if (firstAuthorization) openPanel();
      if (authAttempted) state.log("DEV authorized");
      authAttempted = false;
      wasAuthorized = true;
      startWorldDebug();
    } else if (authAttempted) {
      authState.textContent = "Access denied.";
      auth.hidden = false;
      window.setTimeout(() => password.focus(), 0);
      authAttempted = false;
    } else if (wasAuthorized) {
      wasAuthorized = false;
      state.panelOpen = false;
      panel.hidden = true;
      panelOpen.hidden = true;
      verticalControls.hidden = true;
      game.resetDeveloperLocalState();
      state.reset();
      state.log("DEV authorization ended with WebSocket session");
    }
    if (teleport) {
      streamingAfterTeleport = true;
      commandState.textContent = `Teleported ${teleport.x.toFixed(1)}, ${teleport.z.toFixed(1)}`;
      commandState.dataset.teleport = teleport.target ?? "coordinates";
      state.log(`Teleported ${teleport.target ?? "coordinates"}`);
    }
    refreshButtons(game, state);
  };
  game.network.onDeveloperError = (code) => {
    commandState.textContent = friendlyError(code);
    if (code === "DEV_ITEM_INVALID" || code === "DEV_UNAUTHORIZED") {
      itemState.textContent = friendlyError(code);
    }
    state.log(`Server rejected DEV command: ${code}`);
  };

  panelClose.addEventListener("click", closePanel);
  panelOpen.addEventListener("click", openPanel);
  $("#developer-noclip").addEventListener("click", () => {
    const enabled = !game.network.developer.noclip;
    game.setDeveloperNoclip(enabled);
    state.log(`Noclip ${enabled ? "ON" : "OFF"}`);
  });
  $("#developer-free-fly").addEventListener("click", () => {
    const enabled = !game.network.developer.freeFly;
    game.setDeveloperFreeFly(enabled);
    state.log(`Free fly ${enabled ? "ON" : "OFF"}`);
  });
  $("#developer-freecam").addEventListener("click", () => {
    const enabled = !game.snapshot().dev.freeCam;
    game.setDeveloperFreeCam(enabled);
    state.log(`Freecam ${enabled ? "ON" : "OFF"}`);
    refreshButtons(game, state);
  });
  $("#developer-playercam").addEventListener("click", () => {
    game.setDeveloperFreeCam(false);
    refreshButtons(game, state);
  });
  $("#developer-player-to-camera").addEventListener("click", () => game.teleportPlayerToCamera());
  $("#developer-reset-camera").addEventListener("click", () => {
    game.resetCamera();
    refreshButtons(game, state);
  });

  const speeds = $("#developer-speeds");
  for (const speed of DEVELOPER_SPEEDS) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.speed = String(speed);
    button.textContent = `${speed}×`;
    button.addEventListener("click", () => game.setDeveloperSpeed(speed));
    speeds.append(button);
  }

  const teleports = $("#developer-teleports");
  for (const target of DEVELOPER_TELEPORT_TARGET_IDS) {
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.target = target;
    button.textContent = WORLD_REVIEW_DESTINATIONS[target].label.toUpperCase();
    button.addEventListener("click", () => game.teleportDeveloper(target));
    teleports.append(button);
  }

  $("#developer-teleport-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const x = Number(($("#developer-teleport-x") as HTMLInputElement).value);
    const z = Number(($("#developer-teleport-z") as HTMLInputElement).value);
    if (!Number.isFinite(x) || !Number.isFinite(z) || x < WORLD_MIN_COORDINATE || x >= WORLD_MAX_COORDINATE || z < WORLD_MIN_COORDINATE || z >= WORLD_MAX_COORDINATE) {
      commandState.textContent = `Coordinates must be within ${WORLD_MIN_COORDINATE}…${WORLD_MAX_COORDINATE}.`;
      return;
    }
    game.teleportDeveloperCoordinates(x, z);
  });

  $("#developer-nearest-human").addEventListener("click", () => teleportNearestPoi(game, "human", commandState));
  $("#developer-nearest-ai").addEventListener("click", () => teleportNearestPoi(game, "ai", commandState));
  setupBookmarks(game, $("#developer-bookmarks"), commandState);
  setupItemControls(game, state, itemState);

  const map = $("#developer-biome-map") as HTMLCanvasElement;
  const mapLegend = $("#developer-map-legend") as HTMLElement;
  $("#developer-map-toggle").addEventListener("click", () => {
    const open = map.hidden;
    map.hidden = !open;
    mapLegend.hidden = !open;
    panel.dataset.mapOpen = String(open);
    if (open) {
      const focus = game.snapshot().dev.freeCam ? game.snapshot().dev.freeCamTarget : game.snapshot().player;
      drawMacroBiomeMap(map, focus.x, focus.z);
      biomeMapDrawn = true;
    }
  });

  for (const button of panel.querySelectorAll<HTMLButtonElement>("[data-overlay]")) {
    button.addEventListener("click", () => {
      const name = button.dataset.overlay as keyof DeveloperOverlayState;
      state.setOverlay(name, !state.overlays[name]);
      refreshButtons(game, state);
    });
  }
  setupRenderControls(game, $("#developer-render-controls"));

  const setScreenshotMode = (enabled: boolean): void => {
    if (!game.network.developer.authorized) return;
    state.screenshotMode = enabled;
    document.body.classList.toggle("screenshot-mode", enabled);
    if (enabled) {
      panel.hidden = true;
      panelOpen.hidden = true;
    } else if (state.panelOpen) panel.hidden = false;
    else panelOpen.hidden = false;
    state.log(`Screenshot mode ${enabled ? "ON" : "OFF"}`);
  };
  $("#developer-screenshot").addEventListener("click", () => setScreenshotMode(!state.screenshotMode));
  $("#developer-copy-position").addEventListener("click", () => {
    const player = game.snapshot().player;
    void copyText(`${player.x.toFixed(2)},${player.z.toFixed(2)}`, commandState, "Position copied.");
  });
  $("#developer-copy-state").addEventListener("click", () => {
    void copyText(createDebugReport(game), commandState, "Debug state copied.");
  });

  const reset = (close = false): void => {
    game.network.resetDeveloper();
    game.resetDeveloperLocalState();
    state.reset();
    document.body.classList.remove("screenshot-mode");
    if (close) closePanel();
    refreshButtons(game, state);
  };
  $("#developer-reset").addEventListener("click", () => reset(false));
  $("#developer-panic").addEventListener("click", () => reset(true));

  const verticalControls = $("#developer-vertical-controls") as HTMLElement;
  for (const button of verticalControls.querySelectorAll<HTMLButtonElement>("button")) {
    const value = Number(button.dataset.vertical) as -1 | 1;
    let activePointer: number | undefined;
    const stop = (event: PointerEvent): void => {
      if (event.pointerId !== activePointer) return;
      activePointer = undefined;
      game.input.setDeveloperVertical(0);
    };
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      activePointer = event.pointerId;
      button.setPointerCapture(event.pointerId);
      game.input.setDeveloperVertical(value);
    });
    button.addEventListener("pointerup", stop);
    button.addEventListener("pointercancel", stop);
  }

  document.addEventListener("keydown", (event) => {
    if (event.code === "F2") {
      event.preventDefault();
      if (game.network.developer.authorized) state.panelOpen ? closePanel() : openPanel();
      else openAuth();
      return;
    }
    if (event.code === "F10" && game.network.developer.authorized) {
      event.preventDefault();
      setScreenshotMode(!state.screenshotMode);
      return;
    }
    if (!state.panelOpen || isEditableTarget(event.target)) return;
    const shortcut = /^Digit([1-6])$/.exec(event.code);
    if (shortcut) {
      const speed = DEVELOPER_SPEEDS[Number(shortcut[1]) - 1];
      if (speed) game.setDeveloperSpeed(speed);
    }
  });
  setupLongPress($(".prototype-label"), openAuth);

  const update = (): void => {
    if (!game.network.developer.authorized) return;
    const snapshot = game.snapshot();
    verticalControls.hidden = !(snapshot.network.developer.freeFly || snapshot.dev.freeCam);
    frameHistory.push(snapshot.metrics.frameMs);
    if (frameHistory.length > 40) frameHistory.shift();
    if (streamingAfterTeleport) {
      const ready = snapshot.streaming.pendingChunks === 0 && snapshot.world.pendingChunks === 0;
      streamingAfterTeleport = !ready;
    }
    streaming.hidden = !streamingAfterTeleport;
    if (state.panelOpen && !state.screenshotMode) {
      renderPanel(game, state, frameHistory);
      if (biomeMapDrawn && !map.hidden) {
        const focus = snapshot.dev.freeCam ? snapshot.dev.freeCamTarget : snapshot.player;
        drawMacroBiomeMap(map, focus.x, focus.z);
      }
    }
    refreshButtons(game, state);
  };
  window.setInterval(update, 250);
  refreshButtons(game, state);
  return state;
}

function setupItemControls(game: EndclockGame, state: LocalDeveloperState, status: HTMLElement): void {
  const form = $("#developer-item-form") as HTMLFormElement;
  const definitionInput = $("#developer-item-definition") as HTMLInputElement;
  const quantityInput = $("#developer-item-quantity") as HTMLInputElement;
  const definitions = $("#developer-item-definitions") as HTMLDataListElement;
  const spawnButton = $("#developer-spawn-drop") as HTMLButtonElement;
  const clearButton = $("#developer-clear-inventory") as HTMLButtonElement;
  const copyButton = $("#developer-copy-inventory") as HTMLButtonElement;
  const resetCacheButton = $("#developer-reset-test-cache") as HTMLButtonElement;
  const orderedDefinitions = [...ITEM_DEFINITIONS].sort((left, right) => (
    left.category.localeCompare(right.category)
    || (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
    || left.name.localeCompare(right.name)
  ));
  for (const definition of orderedDefinitions) {
    const option = document.createElement("option");
    option.value = definition.id;
    option.label = `${definition.name} · ${definition.category}`;
    definitions.append(option);
  }
  definitionInput.value = orderedDefinitions[0]?.id ?? "";
  quantityInput.max = String(ITEM_STACK_LIMIT_MAXIMUM);

  const requireAuthorization = (): boolean => {
    if (game.network.developer.authorized) return true;
    status.textContent = "Developer authorization required.";
    return false;
  };
  const readSelection = (worldDrop: boolean): { definitionId: string; name: string; quantity: number } | undefined => {
    if (!requireAuthorization()) return undefined;
    const definitionId = definitionInput.value.trim();
    const definition = getItemDefinition(definitionId);
    const quantity = Number(quantityInput.value);
    if (!definition) {
      status.textContent = "Unknown item definition.";
      definitionInput.focus();
      return undefined;
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > ITEM_STACK_LIMIT_MAXIMUM) {
      status.textContent = `Quantity must be 1…${ITEM_STACK_LIMIT_MAXIMUM}.`;
      quantityInput.focus();
      return undefined;
    }
    if (worldDrop && quantity > definition.stackLimit) {
      status.textContent = `World drop stack limit is ${definition.stackLimit}.`;
      quantityInput.focus();
      return undefined;
    }
    return { definitionId, name: definition.name, quantity };
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const selection = readSelection(false);
    if (!selection) return;
    game.network.developerGiveItem(selection.definitionId, selection.quantity);
    status.textContent = `GIVE requested: ${selection.name} x${selection.quantity}.`;
    state.log(`DEV GIVE ${selection.definitionId} x${selection.quantity}`);
  });
  spawnButton.addEventListener("click", () => {
    const selection = readSelection(true);
    if (!selection) return;
    game.network.developerSpawnWorldItem(selection.definitionId, selection.quantity);
    status.textContent = `WORLD SPAWN requested: ${selection.name} x${selection.quantity}.`;
    state.log(`DEV SPAWN ${selection.definitionId} x${selection.quantity}`);
  });
  resetCacheButton.addEventListener("click", () => {
    if (!requireAuthorization()) return;
    game.network.developerResetTestCache();
    status.textContent = "TEST CACHE reset requested (DEV only).";
    state.log("DEV RESET TEST CACHE");
  });
  copyButton.addEventListener("click", () => {
    if (!requireAuthorization()) return;
    void copyText(createInventoryStateJson(game), status, "Inventory JSON copied.");
  });

  let clearConfirmationExpires = 0;
  let clearConfirmationTimer: number | undefined;
  const cancelClearConfirmation = (): void => {
    clearConfirmationExpires = 0;
    clearButton.textContent = "CLEAR INVENTORY";
    if (clearConfirmationTimer !== undefined) window.clearTimeout(clearConfirmationTimer);
    clearConfirmationTimer = undefined;
  };
  clearButton.addEventListener("click", () => {
    if (!requireAuthorization()) return;
    const now = performance.now();
    if (clearConfirmationExpires === 0 || now > clearConfirmationExpires) {
      clearConfirmationExpires = now + 4_000;
      clearButton.textContent = "CONFIRM CLEAR";
      status.textContent = "Press CONFIRM CLEAR within 4 seconds.";
      clearConfirmationTimer = window.setTimeout(cancelClearConfirmation, 4_000);
      return;
    }
    cancelClearConfirmation();
    game.network.developerClearInventory();
    status.textContent = "Inventory clear requested.";
    state.log("DEV CLEAR INVENTORY");
  });
}

function createInventoryStateJson(game: EndclockGame): string {
  const inventory = game.inventory.snapshot().playerInventory;
  if (!inventory) return JSON.stringify({ inventory: null }, null, 2);
  return JSON.stringify({
    revision: inventory.revision,
    slotCount: inventory.slotCount,
    massGrams: inventory.massGrams,
    maxMassGrams: inventory.maxMassGrams,
    slots: inventory.slots.map((stack, slot) => stack ? {
      slot,
      instanceId: stack.instanceId,
      definitionId: stack.definitionId,
      quantity: stack.quantity,
      condition: stack.durabilityCurrent ?? null,
      quality: stack.quality ?? null,
    } : { slot, empty: true }),
  }, null, 2);
}

function selectedInventoryStackId(snapshot: ClientInventorySnapshot): string | undefined {
  const selected = document.querySelector<HTMLButtonElement>(".inventory-slot[data-selected='true']");
  if (!selected) return undefined;
  const slot = Number(selected.dataset.slot);
  if (!Number.isSafeInteger(slot) || slot < 0) return undefined;
  const slots = selected.dataset.area === "CONTAINER"
    ? snapshot.container?.state.slots
    : snapshot.playerInventory?.slots;
  return slots?.[slot]?.instanceId;
}

function setupLongPress(element: HTMLElement, callback: () => void): void {
  let timer: number | undefined;
  const cancel = (): void => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };
  element.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    cancel();
    timer = window.setTimeout(() => {
      timer = undefined;
      callback();
    }, 1_900);
  });
  for (const name of ["pointerup", "pointercancel", "pointerleave"]) element.addEventListener(name, cancel);
}

function refreshButtons(game: EndclockGame, state: LocalDeveloperState): void {
  const snapshot = game.snapshot();
  const set = (selector: string, active: boolean, label: string): void => {
    const button = $(selector) as HTMLButtonElement;
    button.dataset.active = String(active);
    button.textContent = `${label} ${active ? "ON" : "OFF"}`;
  };
  set("#developer-noclip", snapshot.network.developer.noclip, "NOCLIP");
  set("#developer-free-fly", snapshot.network.developer.freeFly, "FREE FLY");
  set("#developer-freecam", snapshot.dev.freeCam, "FREECAM");
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-speed]")) {
    button.dataset.active = String(Number(button.dataset.speed) === snapshot.network.developer.speed);
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-overlay]")) {
    const name = button.dataset.overlay as keyof DeveloperOverlayState;
    button.dataset.active = String(state.overlays[name]);
  }
}

function renderPanel(game: EndclockGame, state: LocalDeveloperState, frames: readonly number[]): void {
  const value = game.snapshot();
  const player = value.player;
  const terrain = sampleWorldTerrainAt(player.x, player.z);
  $("#developer-player-info").textContent = [
    `PLAYER X/Y/Z  ${player.x.toFixed(2)} / ${player.y.toFixed(2)} / ${player.z.toFixed(2)}`,
    `GROUND HEIGHT ${player.groundHeight.toFixed(2)}   CHUNK ${value.streaming.currentChunk.x}/${value.streaming.currentChunk.z}`,
    `SPEED ${player.speed.toFixed(2)} m/s   VX/VY/VZ ${player.velocityX.toFixed(2)} / ${player.velocityY.toFixed(2)} / ${player.velocityZ.toFixed(2)}`,
    `FACING/YAW ${value.character.localYaw.toFixed(3)}`,
    `PREDICTED ${player.x.toFixed(2)}, ${player.y.toFixed(2)}, ${player.z.toFixed(2)}`,
    `SERVER ${player.authoritativeX.toFixed(2)}, ${player.authoritativeY.toFixed(2)}, ${player.authoritativeZ.toFixed(2)}`,
    `RECON ERROR ${player.reconciliationError.toFixed(3)}  CORRECTIONS ${player.reconciliationCorrections}`,
    `INPUT ${value.network.latestInput}  ACK ${value.network.acknowledgedInput}  PENDING ${value.network.pendingInputs}`,
  ].join("\n");
  $("#developer-world-info").textContent = [
    `TERRAIN H ${terrain.height.toFixed(2)}  NORMAL ${terrain.normal.map((part) => part.toFixed(2)).join("/")}  SLOPE ${(Math.acos(terrain.normal[1]) * 180 / Math.PI).toFixed(1)}°`,
    `SURFACE dirt/ash/dark/dead/green/road/river`,
    `        ${terrain.surfaceWeights.map((weight) => weight.toFixed(2)).join("/")}`,
    `BIOME ${terrain.macroBiome.primary}  SECONDARY ${terrain.macroBiome.secondary ?? "—"} ${(terrain.macroBiome.secondaryWeight * 100).toFixed(0)}%`,
    `BOUNDARY ${terrain.macroBiome.distanceToBoundary.toFixed(1)}m  TRANS ${(terrain.macroBiome.transition * 100).toFixed(0)}%`,
    `REGIONS ${Object.entries(terrain.regions).map(([name, weight]) => `${name}:${weight.toFixed(2)}`).join(" ")}`,
    `RADIATION ${terrain.radiation.intensity.toFixed(3)} ${terrain.radiation.level}  ECO ${terrain.ecology.protectionLevel.toFixed(3)}`,
    `ROAD ${terrain.road.roadClass ?? "—"} dist ${terrain.road.distance.toFixed(1)}  RIVER dist ${terrain.river.distance.toFixed(1)}`,
  ].join("\n");
  const records = game.world.developerRecords();
  const nearestObject = records.objects.reduce<typeof records.objects[number] | undefined>((best, object) => (
    !best || distance(object.x, object.z, player.x, player.z) < distance(best.x, best.z, player.x, player.z) ? object : best
  ), undefined);
  const nearestPoi = records.pois.reduce<typeof records.pois[number] | undefined>((best, poi) => (
    !best || distance(poi.x, poi.z, player.x, player.z) < distance(best.x, best.z, player.x, player.z) ? poi : best
  ), undefined);
  $("#developer-object-info").textContent = [
    `COLLIDERS TESTED ${value.metrics.collisionCandidates}  AVG ${value.metrics.collisionCandidatesAverage.toFixed(1)}  MAX ${value.metrics.collisionCandidatesMax}`,
    `PLAYER RADIUS 0.28  COLLIDER CACHE ${value.world.collisionCacheChunks}`,
    `NEAREST POI ${nearestPoi ? `${nearestPoi.archetype} ${nearestPoi.id} @ ${nearestPoi.x.toFixed(1)},${nearestPoi.z.toFixed(1)}` : "—"}`,
    `POI FOOTPRINT/EXCLUSION ${nearestPoi ? `${nearestPoi.footprintRadius.toFixed(1)} / ${nearestPoi.exclusionRadius.toFixed(1)}` : "—"}`,
    `OBJECT ID ${nearestObject?.id ?? "—"}`,
    `TYPE/ASSET ${nearestObject ? `${nearestObject.type} / ${nearestObject.asset}` : "—"}`,
    `POSITION ${nearestObject ? `${nearestObject.x.toFixed(2)}, ${nearestObject.y.toFixed(2)}, ${nearestObject.z.toFixed(2)}` : "—"}`,
    `OWNER CHUNK ${nearestObject ? `${nearestObject.chunkX}/${nearestObject.chunkZ}` : "—"}  YAW ${nearestObject?.yaw.toFixed(3) ?? "—"} SCALE ${nearestObject?.scale.toFixed(2) ?? "—"}`,
    `POI PARENT ${nearestObject?.poiParent ?? "—"}`,
  ].join("\n");
  const interaction = value.interaction;
  $("#developer-interaction-info").textContent = [
    `CURRENT TARGET ID ${interaction.targetId ?? "—"}`,
    `TARGET TYPE ${interaction.targetType ?? "—"}`,
    `INTERACTION PROFILE ${interaction.profileId ?? "—"}`,
    `AVAILABLE ACTION ${interaction.actionId ? `${interaction.actionId} / ${interaction.actionLabel ?? "—"}` : "—"}`,
    `DISTANCE / MAX RANGE ${interaction.distance === null ? "—" : interaction.distance.toFixed(3)} / ${interaction.maxRange === null ? "—" : interaction.maxRange.toFixed(2)} m`,
    `LINE OF SIGHT ${interaction.targetId === null ? "—" : interaction.lineOfSight ? "CLEAR" : "BLOCKED"}`,
    `OBJECT STATE ${interaction.objectState ?? "—"}`,
    `OVERRIDE REVISION ${interaction.revision}`,
    `OVERRIDE CACHE ${value.worldOverrides.count} objects / ${value.worldOverrides.chunkCount} chunks / highest ${value.worldOverrides.highestRevision}`,
    `ACTIVE INTERACTION ${interaction.activeRequestId ? `${interaction.state} ${interaction.activeRequestId}` : interaction.state}`,
    `PROGRESS ${(interaction.progress * 100).toFixed(1)}%${interaction.cancelling ? "  CANCELLING" : ""}`,
    `LAST RESULT CODE ${interaction.lastResult ?? "—"}`,
    `CANDIDATES / QUERY ${interaction.candidateCount} / ${interaction.targetQueryMs.toFixed(3)} ms`,
  ].join("\n");
  const inventory = game.inventory.snapshot();
  const playerInventory = inventory.playerInventory;
  let pendingRequest: readonly [string, string] | undefined;
  for (const entry of inventory.pendingRequests) pendingRequest = entry;
  $("#developer-inventory-info").textContent = [
    `REVISION ${playerInventory?.revision ?? "—"}`,
    `MASS ${playerInventory ? formatMass(playerInventory.massGrams).toUpperCase() : "—"} / ${playerInventory?.maxMassGrams === null ? "UNLIMITED" : playerInventory ? formatMass(playerInventory.maxMassGrams).toUpperCase() : "—"}`,
    `SLOTS ${playerInventory?.slots.filter(Boolean).length ?? 0} / ${playerInventory?.slotCount ?? "—"}`,
    `SELECTED STACK ${selectedInventoryStackId(inventory) ?? "—"}`,
    `LAST REQUEST ${pendingRequest?.[0] ?? inventory.lastResult?.requestId ?? "—"}`,
    `LAST RESULT ${pendingRequest ? `${pendingRequest[1]} / PENDING` : inventory.lastResult ? `${inventory.lastResult.action} / ${inventory.lastResult.result}` : "—"}`,
    `CONTAINER SESSION ${inventory.container?.accessSessionId ?? "—"}`,
    `CONTAINER REVISION ${inventory.container?.state.revision ?? "—"}`,
    `NEARBY DROPPED ENTITIES ${inventory.worldDrops.size}`,
  ].join("\n");
  $("#developer-network-info").textContent = [
    `STATE ${value.network.state}  PING ${value.network.ping.toFixed(0)}ms  TICK ${value.network.tick}`,
    `SNAPSHOT AGE ${value.network.snapshotAge.toFixed(0)}ms  RECONNECTS ${value.network.reconnects}`,
    `BYTES SENT/RECEIVED ${value.network.sent}/${value.network.received}  BUFFERED ${value.network.buffered}`,
    `REMOTE/RELEVANT ${value.character.remoteCount}/${value.network.latestInput >= 0 ? value.character.remoteCount + 1 : 0}`,
    `PREDICTION ERR ${player.reconciliationError.toFixed(3)}  INPUT/ACK ${value.network.latestInput}/${value.network.acknowledgedInput}`,
  ].join("\n");
  const heap = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize;
  $("#developer-performance-info").textContent = [
    `FPS ${value.metrics.fps.toFixed(0)}  FRAME ${value.metrics.frameMs.toFixed(2)}ms  CPU ${value.metrics.cpuMs.toFixed(2)}ms`,
    `DRAWS ${value.metrics.drawCalls}  TRIANGLES ${value.metrics.triangles}`,
    `MESH ${(value.meshBytes / 1_048_576).toFixed(2)}MiB  INST ${(value.metrics.instanceBufferBytes / 1_024).toFixed(1)}KiB  SURFACE ${(value.metrics.surfaceDataBytes / 1_024).toFixed(1)}KiB`,
    `JS HEAP ${heap ? `${(heap / 1_048_576).toFixed(1)}MiB` : "unavailable"}`,
    `TERRAIN visible/loaded/ready ${value.streaming.visibleChunks}/${value.streaming.loadedChunks}/${value.streaming.readyActiveChunks}`,
    `TERRAIN pending/cpu/gpu/failed ${value.streaming.pendingChunks}/${value.streaming.cpuReadyChunks}/${value.streaming.gpuReadyChunks}/${value.streaming.failedChunks}`,
    `GEN avg/max ${value.streaming.generationMsAverage.toFixed(2)}/${value.streaming.generationMsMax.toFixed(2)}ms  UP ${value.streaming.uploadMsAverage.toFixed(2)}/${value.streaming.uploadMsMax.toFixed(2)}ms`,
    `OBJECT active/cache/pending/failed ${value.world.activeChunks}/${value.world.cachedChunks}/${value.world.pendingChunks}/${value.world.failedChunks}`,
    `VISIBLE trees ${value.metrics.visibleVegetationTrees} rocks ${value.metrics.visibleGeologyInstances} human ${value.metrics.visibleHumanObjects} ai ${value.metrics.visibleAiObjects} chars ${value.metrics.visibleCharacters}`,
  ].join("\n");
  $("#developer-event-log").textContent = state.events.slice(-12).map((event) => `${new Date(event.at).toISOString().slice(11, 19)} ${event.message}`).join("\n") || "No DEV events.";
  drawFrameGraph($("#developer-frame-graph") as HTMLCanvasElement, frames);
  refreshRenderControls(game);
}

function setupRenderControls(game: EndclockGame, container: HTMLElement): void {
  const controls: readonly { key: string; label: string; type: "render" | "atmosphere" | "aiGlow" }[] = [
    { key: "fog", label: "FOG", type: "atmosphere" }, { key: "lighting", label: "LIGHTING", type: "atmosphere" },
    { key: "grading", label: "COLOR GRADING", type: "atmosphere" }, { key: "particles", label: "PARTICLES", type: "render" },
    { key: "aiGlow", label: "AI GLOW", type: "aiGlow" }, { key: "water", label: "WATER", type: "render" },
    { key: "vegetation", label: "VEGETATION", type: "render" }, { key: "geology", label: "GEOLOGY", type: "render" },
    { key: "human", label: "HUMAN OBJECTS", type: "render" }, { key: "ai", label: "AI OBJECTS", type: "render" },
    { key: "characters", label: "CHARACTERS", type: "render" }, { key: "terrain", label: "TERRAIN", type: "render" },
  ];
  for (const control of controls) {
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.type = "checkbox";
    input.dataset.renderKey = control.key;
    input.dataset.renderType = control.type;
    input.addEventListener("change", () => {
      if (control.type === "render") game.setDeveloperRenderFlag(control.key as keyof DeveloperRenderFlags, input.checked);
      else if (control.type === "atmosphere") game.setDeveloperAtmosphereFlag(control.key as keyof AtmosphereFeatureFlags, input.checked);
      else game.setAiGlowEnabled(input.checked);
    });
    label.append(input, control.label);
    container.append(label);
  }
  refreshRenderControls(game);
}

function refreshRenderControls(game: EndclockGame): void {
  const snapshot = game.snapshot();
  for (const input of document.querySelectorAll<HTMLInputElement>("[data-render-key]")) {
    const key = input.dataset.renderKey!;
    const type = input.dataset.renderType;
    input.checked = type === "render"
      ? snapshot.dev.render[key as keyof DeveloperRenderFlags]
      : type === "atmosphere"
        ? snapshot.atmosphere.flags[key as keyof AtmosphereFeatureFlags]
        : snapshot.ai.glowEnabled;
  }
}

function setupBookmarks(game: EndclockGame, container: HTMLElement, status: HTMLElement): void {
  const read = (): ({ x: number; z: number } | null)[] => {
    try {
      const value = JSON.parse(localStorage.getItem(BOOKMARK_KEY) ?? "[]") as unknown;
      if (!Array.isArray(value)) return [null, null, null];
      return [0, 1, 2].map((index) => {
        const item = value[index] as { x?: unknown; z?: unknown } | undefined;
        return item && Number.isFinite(item.x) && Number.isFinite(item.z) ? { x: Number(item.x), z: Number(item.z) } : null;
      });
    } catch {
      return [null, null, null];
    }
  };
  const bookmarks = read();
  for (let index = 0; index < 3; index += 1) {
    const slot = document.createElement("div");
    const save = document.createElement("button");
    const go = document.createElement("button");
    save.type = go.type = "button";
    save.textContent = `SAVE ${index + 1}`;
    const refresh = (): void => {
      const bookmark = bookmarks[index];
      go.textContent = bookmark ? `GO ${index + 1}` : `EMPTY ${index + 1}`;
      go.disabled = !bookmark;
    };
    save.addEventListener("click", () => {
      const player = game.snapshot().player;
      bookmarks[index] = { x: player.x, z: player.z };
      localStorage.setItem(BOOKMARK_KEY, JSON.stringify(bookmarks));
      status.textContent = `Bookmark ${index + 1} saved.`;
      refresh();
    });
    go.addEventListener("click", () => {
      const bookmark = bookmarks[index];
      if (bookmark) game.teleportDeveloperCoordinates(bookmark.x, bookmark.z);
    });
    slot.append(save, go);
    container.append(slot);
    refresh();
  }
}

function teleportNearestPoi(game: EndclockGame, origin: "human" | "ai", status: HTMLElement): void {
  const player = game.snapshot().player;
  const poi = game.world.developerRecords().pois.filter((candidate) => candidate.origin === origin).sort((left, right) => (
    distance(left.x, left.z, player.x, player.z) - distance(right.x, right.z, player.x, player.z)
  ))[0];
  if (!poi) {
    status.textContent = `No nearby ${origin.toUpperCase()} site in streamed chunks.`;
    return;
  }
  game.teleportDeveloperCoordinates(poi.x, poi.z);
}

function createDebugReport(game: EndclockGame): string {
  const value = game.snapshot();
  const terrain = sampleWorldTerrainAt(value.player.x, value.player.z);
  return [
    `ENDCLOCK ${$("#build-version").textContent?.trim() || "BUILD"}`,
    `PLAYER ${value.player.x.toFixed(2)},${value.player.y.toFixed(2)},${value.player.z.toFixed(2)}`,
    `CHUNK ${value.streaming.currentChunk.id}`,
    `BIOME ${terrain.macroBiome.primary}/${terrain.macroBiome.secondary ?? "none"}`,
    `REGIONS ${JSON.stringify(terrain.regions)}`,
    `RADIATION ${terrain.radiation.intensity.toFixed(3)} ECO ${terrain.ecology.protectionLevel.toFixed(3)}`,
    `FPS ${value.metrics.fps.toFixed(0)} FRAME ${value.metrics.frameMs.toFixed(2)}ms DRAWS ${value.metrics.drawCalls} TRI ${value.metrics.triangles}`,
    `GPU MESH ${value.meshBytes} INSTANCE ${value.metrics.instanceBufferBytes} SURFACE ${value.metrics.surfaceDataBytes}`,
    `STREAM terrain=${value.streaming.loadedChunks}/${value.streaming.pendingChunks}/${value.streaming.failedChunks} objects=${value.world.cachedChunks}/${value.world.pendingChunks}/${value.world.failedChunks}`,
    `NETWORK ${value.network.state} ping=${value.network.ping.toFixed(0)}ms tick=${value.network.tick} buffered=${value.network.buffered}`,
    `NEAREST POI ${value.world.nearestPoi ?? "none"}`,
    `INTERACTION target=${value.interaction.targetId ?? "none"} state=${value.interaction.objectState ?? "none"} revision=${value.interaction.revision} active=${value.interaction.state} result=${value.interaction.lastResult ?? "none"}`,
    `DEV noclip=${value.network.developer.noclip} fly=${value.network.developer.freeFly} freecam=${value.dev.freeCam} speed=${value.network.developer.speed}x`,
  ].join("\n");
}

async function copyText(text: string, status: HTMLElement, success: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    status.textContent = success;
  } catch {
    status.textContent = "Clipboard unavailable.";
  }
}

function drawFrameGraph(canvas: HTMLCanvasElement, values: readonly number[]): void {
  const context = canvas.getContext("2d");
  if (!context) return;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "rgba(126, 211, 160, .9)";
  context.lineWidth = 1;
  context.beginPath();
  values.forEach((value, index) => {
    const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * canvas.width;
    const y = canvas.height - Math.min(50, value) / 50 * canvas.height;
    if (index === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  });
  context.stroke();
  context.strokeStyle = "rgba(238, 183, 91, .45)";
  const y16 = canvas.height - 16.67 / 50 * canvas.height;
  context.beginPath();
  context.moveTo(0, y16);
  context.lineTo(canvas.width, y16);
  context.stroke();
}

function friendlyError(code: string): string {
  if (code === "DEV_UNAUTHORIZED") return "Developer authorization required.";
  if (code === "DEV_COORDINATES_INVALID") return "Coordinates rejected by server.";
  return `Server error: ${code}`;
}

function distance(ax: number, az: number, bx: number, bz: number): number {
  return Math.hypot(ax - bx, az - bz);
}

const BIOME_MAP_COLORS: Readonly<Record<BiomeId, readonly [number, number, number]>> = Object.freeze({
  ASHLANDS: [190, 96, 46], GROUND_ZERO: [71, 59, 86], FERTILE_PRESERVE: [104, 177, 70],
  NUCLEAR_FOREST: [20, 91, 59], RUINED_CIVILIZATION: [181, 170, 143], INDUSTRIAL_RUINS: [133, 99, 102],
});
let biomeMapBase: ImageData | undefined;

function drawMacroBiomeMap(canvas: HTMLCanvasElement, playerX: number, playerZ: number): void {
  const context = canvas.getContext("2d");
  if (!context) return;
  if (!biomeMapBase || biomeMapBase.width !== canvas.width || biomeMapBase.height !== canvas.height) {
    biomeMapBase = createMacroBiomeMapBase(context, canvas.width, canvas.height);
  }
  context.putImageData(biomeMapBase, 0, 0);
  const worldSize = WORLD_MAX_COORDINATE - WORLD_MIN_COORDINATE;
  const toMap = (worldX: number, worldZ: number): readonly [number, number] => [
    ((worldX - WORLD_MIN_COORDINATE) / worldSize) * canvas.width,
    ((WORLD_MAX_COORDINATE - worldZ) / worldSize) * canvas.height,
  ];
  context.font = "bold 7px monospace";
  for (const destination of Object.values(WORLD_REVIEW_DESTINATIONS)) {
    const [x, y] = toMap(destination.x, destination.z);
    context.fillStyle = "#ff9129";
    context.fillRect(x - 1.5, y - 1.5, 3, 3);
  }
  const [x, y] = toMap(playerX, playerZ);
  context.strokeStyle = "#fff";
  context.lineWidth = 1.5;
  context.beginPath(); context.arc(x, y, 3, 0, Math.PI * 2); context.stroke();
}

function createMacroBiomeMapBase(context: CanvasRenderingContext2D, width: number, height: number): ImageData {
  const image = context.createImageData(width, height);
  const worldSize = WORLD_MAX_COORDINATE - WORLD_MIN_COORDINATE;
  const metresPerPixel = worldSize / width;
  for (let pixelZ = 0; pixelZ < height; pixelZ += 1) {
    for (let pixelX = 0; pixelX < width; pixelX += 1) {
      const worldX = WORLD_MIN_COORDINATE + ((pixelX + 0.5) / width) * worldSize;
      const worldZ = WORLD_MAX_COORDINATE - ((pixelZ + 0.5) / height) * worldSize;
      const biome = sampleMacroBiomeAt(worldX, worldZ);
      const primary = BIOME_MAP_COLORS[biome.primary];
      const secondary = biome.secondary ? BIOME_MAP_COLORS[biome.secondary] : primary;
      let red = primary[0] + (secondary[0] - primary[0]) * biome.secondaryWeight;
      let green = primary[1] + (secondary[1] - primary[1]) * biome.secondaryWeight;
      let blue = primary[2] + (secondary[2] - primary[2]) * biome.secondaryWeight;
      const road = getWorldRoadProfileAt(worldX, worldZ);
      const river = getWorldRiverProfileAt(worldX, worldZ);
      if (road.distance <= Math.max(road.width * .58, metresPerPixel * .42)) [red, green, blue] = [238, 210, 89];
      if (river.distance <= Math.max(river.width * .62, metresPerPixel * .4)) [red, green, blue] = [39, 151, 204];
      const offset = (pixelZ * width + pixelX) * 4;
      image.data[offset] = red; image.data[offset + 1] = green; image.data[offset + 2] = blue; image.data[offset + 3] = 255;
    }
  }
  return image;
}
